import { createContext, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { apiBaseUrl, isDemoMode } from "shared/config/appConfig";

const PROGRAMS_API_URL = `${apiBaseUrl}/programs`;

const ProgramsContext = createContext(null);

export function ProgramsProvider({ children }) {
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPrograms = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(PROGRAMS_API_URL);

      if (!response.ok) {
        throw new Error("Error cargando programas");
      }

      const data = await response.json();
      setPrograms(data.content);
      setLoading(false);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  // --------------------------
  // Acciones
  // --------------------------
  const selectProgram = (programId) => {
    setSelectedProgramId(Number(programId));
  };

  const duplicateProgram = async (programId) => {
    if (isDemoMode) {
      throw new Error("Program duplication is disabled in demo mode.");
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Obtener programa original
      const response = await fetch(`${PROGRAMS_API_URL}/${programId}`);

      if (!response.ok) {
        throw new Error("Error fetching program to duplicate");
      }

      const program = await response.json();

      const duplicatedProgram = {
        ...program,
        id: null,
        programExercises: program.programExercises.map(({ id, exerciseName, ...rest }) => ({
          ...rest,
        })),
      };

      const createResponse = await fetch(PROGRAMS_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(duplicatedProgram),
      });

      if (!createResponse.ok) {
        throw new Error("Error creating duplicated program");
      }

      await fetchPrograms();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteProgram = async (programId) => {
    if (isDemoMode) {
      throw new Error("Program deletion is disabled in demo mode.");
    }

    await fetch(`${PROGRAMS_API_URL}/${programId}`, {
      method: "DELETE",
    });

    setPrograms((prev) => prev.filter((p) => p.id !== programId));
  };

  return (
    <ProgramsContext.Provider
      value={{
        programs,
        loading,
        error,
        selectedProgramId,
        selectProgram,
        deleteProgram,
        duplicateProgram,
        fetchPrograms,
      }}
    >
      {children}
    </ProgramsContext.Provider>
  );
}

ProgramsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Hook
export function usePrograms() {
  const context = useContext(ProgramsContext);
  if (!context) {
    throw new Error("usePrograms must be used within a ProgramsProvider");
  }
  return context;
}
